import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-quill/dist/quill.snow.css';

let descriptionIdSeq = 0;

export default function PreviewDescription({ html, collapsedLineClamp = 4 }) {
  const { t } = useTranslation('layout');
  const htmlRef = useRef(null);
  const descriptionIdRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  if (descriptionIdRef.current == null) {
    descriptionIdSeq += 1;
    descriptionIdRef.current = `package-preview-description-${descriptionIdSeq}`;
  }

  const descriptionId = descriptionIdRef.current;

  useLayoutEffect(() => {
    const node = htmlRef.current;
    if (!node) return undefined;

    const measure = () => {
      setIsOverflowing(node.scrollHeight - node.clientHeight > 1);
    };

    if (expanded) return undefined;

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [html, expanded, collapsedLineClamp]);

  const canToggle = isOverflowing || expanded;

  return (
    <div className='preview-description-below'>
      <div className='ql-editor preview-description-below__body'>
        <div
          className={[
            'preview-description-below__html',
            expanded ? '' : 'preview-description-below__html--collapsed',
          ]
            .filter(Boolean)
            .join(' ')}
          dangerouslySetInnerHTML={{ __html: html }}
          id={descriptionId}
          ref={htmlRef}
          style={
            expanded
              ? undefined
              : {
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: collapsedLineClamp,
                  overflow: 'hidden',
                  maxHeight: `calc(1.6em * ${collapsedLineClamp})`,
                }
          }
        />
        {!expanded && isOverflowing ? (
          <div aria-hidden='true' className='preview-description-below__fade' />
        ) : null}
      </div>

      {canToggle ? (
        <button
          aria-controls={descriptionId}
          aria-expanded={expanded}
          className='preview-description-below__toggle'
          onClick={() => setExpanded((value) => !value)}
          type='button'
        >
          {expanded ? t('packages.preview.showLess') : t('packages.preview.showMore')}
          <svg
            aria-hidden='true'
            className={[
              'preview-description-below__chevron',
              expanded ? 'preview-description-below__chevron--expanded' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            focusable='false'
            viewBox='0 0 24 24'
          >
            <path d='M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z' fill='currentColor' />
          </svg>
        </button>
      ) : null}
    </div>
  );
}
